import type { CreateExpensePayload } from '@/features/finance/api'
import type { ExpenseRequest, RequestHistoryItem } from '@/types'
import type { ExpenseDocument } from './schema'
import { REFUND } from '@/features/finance/labels'

function payloadOf(doc: ExpenseDocument): CreateExpensePayload {
  return JSON.parse(doc.payload) as CreateExpensePayload
}

function offlineTeam(payload: CreateExpensePayload) {
  return payload.team_id
    ? { id: payload.team_id, name: `Department ${payload.team_id}` }
    : { id: null, name: 'Unassigned' }
}

export function projectOfflineExpenseToHistory(
  doc: ExpenseDocument,
  requesterName = 'You',
  orgCurrency = 'NGN',
): RequestHistoryItem {
  const payload = payloadOf(doc)
  return {
    key: `offline-expense:${doc.rxdb_id}`,
    id: doc.server_id ?? doc.local_id,
    request_id: doc.server_id ?? doc.local_id,
    request_type: 'expense',
    subtype: payload.category ?? 'other',
    title: payload.title,
    summary: payload.description ?? `${REFUND} claim queued while offline`,
    status: 'pending',
    requester_id: doc.user_id,
    requester_name: requesterName,
    team_id: payload.team_id,
    team_name: offlineTeam(payload).name,
    amount: String(payload.amount),
    currency: payload.currency ?? orgCurrency,
    leave_type: null,
    start_date: null,
    end_date: null,
    days_count: null,
    handover_notes: null,
    decision_notes: doc.last_error,
    project_id: payload.project_id ?? null,
    budget_id: payload.budget_id ?? null,
    receipt_count: doc.receipts.length,
    created_at: doc.created_at,
    updated_at: doc.updated_at,
    source_path: '/app/finance/expenses',
    offline_sync_status: doc.sync_status,
  }
}

export function projectOfflineExpenseToRequest(
  doc: ExpenseDocument,
  user: ExpenseRequest['user'] = null,
  orgCurrency = 'NGN',
): ExpenseRequest {
  const payload = payloadOf(doc)
  return {
    id: doc.server_id ?? doc.local_id,
    org_id: doc.org_id,
    user_id: doc.user_id,
    team_id: payload.team_id,
    title: payload.title,
    description: payload.description ?? null,
    amount: String(payload.amount),
    currency: payload.currency ?? orgCurrency,
    category: (payload.category ?? 'other') as ExpenseRequest['category'],
    expense_date: payload.expense_date ?? null,
    receipt_url: null,
    receipt_count: doc.receipts.length,
    project_id: payload.project_id ?? null,
    budget_id: payload.budget_id ?? null,
    budget_item_id: payload.budget_item_id ?? null,
    budget_department_allocation_id: payload.budget_department_allocation_id ?? null,
    project: null,
    budget: null,
    budget_item: null,
    budget_department_allocation: null,
    requires_approval: true,
    approval_status: 'pending',
    reviewed_by: null,
    reviewed_at: null,
    approved_by: null,
    approved_at: null,
    decision_notes: doc.last_error,
    // A queued claim has not even synced, let alone been paid.
    disbursement_id: null,
    paid: false,
    created_at: doc.created_at,
    updated_at: doc.updated_at,
    user,
    offline_sync_status: doc.sync_status,
  }
}
